import React, { useState, useEffect } from 'react';
import {
  ShieldCheck, Database, Search, Tag, GitBranch, BarChart3,
  Lock, BookOpen, RefreshCw, AlertTriangle, FileText, Settings,
  CheckCircle2, XCircle, Clock, ExternalLink, Play, DollarSign, X,
  Edit2, Plus, ChevronRight, ArrowLeft, Save, Bot, Send
} from 'lucide-react';
import * as purviewService from '../../services/purviewGovernanceService';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const PURVIEW_LABELS = {
  pt: {
    tabs: {
      overview: 'Visão Geral', sources: 'Fontes', catalog: 'Catálogo', classifications: 'Classificações',
      lineage: 'Linhagem', quality: 'Qualidade', policies: 'Políticas', glossary: 'Glossário',
      scans: 'Scans', alerts: 'Alertas', costs: 'Custos', agent: 'AI Agent', audit: 'Auditoria', settings: 'Config',
    },
    statusLabels: {
      active: 'Ativo', ok: 'OK', warning: 'Alerta', pending: 'Pendente', acknowledged: 'Reconhecido',
      investigating: 'Investigando', resolved: 'Resolvido', dismissed: 'Ignorado', critical: 'Crítico',
      high: 'Alto', medium: 'Médio', block: 'Bloquear', warn: 'Avisar', monitor_only: 'Monitor',
    },
    subtitle: 'Catálogo, classificação, linhagem, qualidade e compliance',
  },
  en: {
    tabs: {
      overview: 'Overview', sources: 'Sources', catalog: 'Catalog', classifications: 'Classifications',
      lineage: 'Lineage', quality: 'Quality', policies: 'Policies', glossary: 'Glossary',
      scans: 'Scans', alerts: 'Alerts', costs: 'Costs', agent: 'AI Agent', audit: 'Audit', settings: 'Settings',
    },
    statusLabels: {
      active: 'Active', ok: 'OK', warning: 'Warning', pending: 'Pending', acknowledged: 'Acknowledged',
      investigating: 'Investigating', resolved: 'Resolved', dismissed: 'Dismissed', critical: 'Critical',
      high: 'High', medium: 'Medium', block: 'Block', warn: 'Warn', monitor_only: 'Monitor',
    },
    subtitle: 'Catalog, classification, lineage, quality and compliance',
  }
};

// ─── Design System ─────────────────────────────────────────────────────────
const THEME = {
  primary: '#064e3b',
  gold: '#b48c2c',
  bg: '#f8fafc',
  white: '#ffffff',
  text: '#1f2937',
  textLight: '#6b7280',
};

const TABS = [
  { key: 'overview', icon: ShieldCheck },
  { key: 'sources', icon: Database },
  { key: 'catalog', icon: Search },
  { key: 'classifications', icon: Tag },
  { key: 'lineage', icon: GitBranch },
  { key: 'quality', icon: BarChart3 },
  { key: 'policies', icon: Lock },
  { key: 'glossary', icon: BookOpen },
  { key: 'scans', icon: RefreshCw },
  { key: 'alerts', icon: AlertTriangle },
  { key: 'costs', icon: DollarSign },
  { key: 'agent', icon: Bot },
  { key: 'audit', icon: FileText },
  { key: 'settings', icon: Settings },
];

// ─── Reusable Components ───────────────────────────────────────────────────
const Card = ({ title, value, subtitle, color = THEME.primary }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all">
    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{title}</p>
    <p className="text-2xl font-bold" style={{ color }}>{value}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

const StatusBadge = ({ status, t }) => {
  const ptLabels = {
    active: 'Ativo', ok: 'OK', warning: 'Alerta', pending: 'Pendente', acknowledged: 'Reconhecido',
    investigating: 'Investigando', resolved: 'Resolvido', dismissed: 'Ignorado', critical: 'Crítico',
    high: 'Alto', medium: 'Médio', block: 'Bloquear', warn: 'Avisar', monitor_only: 'Monitor',
  };
  const labels = (t?.statusLabels) || ptLabels;
  const map = {
    active: { bg: '#d1fae5', text: '#065f46' },
    ok: { bg: '#d1fae5', text: '#065f46' },
    warning: { bg: '#fef3c7', text: '#92400e' },
    pending: { bg: '#e5e7eb', text: '#4b5563' },
    acknowledged: { bg: '#dbeafe', text: '#1e40af' },
    investigating: { bg: '#ede9fe', text: '#5b21b6' },
    resolved: { bg: '#d1fae5', text: '#065f46' },
    dismissed: { bg: '#f3f4f6', text: '#4b5563' },
    critical: { bg: '#fee2e2', text: '#991b1b' },
    high: { bg: '#fed7aa', text: '#9a3412' },
    medium: { bg: '#fef3c7', text: '#92400e' },
    block: { bg: '#fee2e2', text: '#991b1b' },
    warn: { bg: '#fef3c7', text: '#92400e' },
    monitor_only: { bg: '#dbeafe', text: '#1e40af' },
  };
  const s = map[status] || { bg: '#e5e7eb', text: '#4b5563' };
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: s.bg, color: s.text }}>
      {labels[status] || status}
    </span>
  );
};

const SectionTitle = ({ children }) => (
  <h3 className="text-lg font-bold mb-4" style={{ color: THEME.primary }}>{children}</h3>
);

// ─── Tab Content Components ────────────────────────────────────────────────

const OverviewTab = ({ data }) => {
  if (!data) return <LoadingState />;
  const { cards, quality_score } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card title="Assets Catalogados" value={cards.total_assets} />
        <Card title="Fontes Conectadas" value={cards.sources_connected} />
        <Card title="Dados Sensíveis" value={cards.sensitive_data_found} color="#dc2626" />
        <Card title="Scans Recentes" value={cards.recent_scans} />
        <Card title="Sem Owner" value={cards.assets_no_owner} color={cards.assets_no_owner > 0 ? '#f59e0b' : THEME.primary} />
        <Card title="Sem Classificação" value={cards.assets_no_classification} color={cards.assets_no_classification > 0 ? '#f59e0b' : THEME.primary} />
        <Card title="Alertas Críticos" value={cards.critical_alerts} color={cards.critical_alerts > 0 ? '#dc2626' : THEME.primary} />
        <Card title="Quality Score" value={`${quality_score}%`} subtitle="Média geral" color={quality_score >= 80 ? '#059669' : '#f59e0b'} />
      </div>
    </div>
  );
};

const SourcesTab = ({ data }) => {
  if (!data) return <LoadingState />;
  return (
    <div>
      <SectionTitle>Fontes de Dados Registradas</SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-gray-500">
            <th className="pb-2">Fonte</th><th className="pb-2">Tipo</th><th className="pb-2">Ambiente</th>
            <th className="pb-2">Status</th><th className="pb-2">Último Scan</th><th className="pb-2">Assets</th><th className="pb-2">Owner</th>
          </tr></thead>
          <tbody>
            {data.map(s => (
              <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 font-medium">{s.name}</td>
                <td className="py-2 text-gray-500">{s.type}</td>
                <td className="py-2"><span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{s.environment}</span></td>
                <td className="py-2"><StatusBadge status={s.status} /></td>
                <td className="py-2 text-gray-500">{s.last_scan ? new Date(s.last_scan).toLocaleDateString('pt-BR') : '—'}</td>
                <td className="py-2 font-medium">{s.asset_count}</td>
                <td className="py-2 text-gray-500">{s.owner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CatalogTab = ({ data }) => {
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const filtered = (data || []).filter(a => !search || a.name.toLowerCase().includes(search.toLowerCase()));

  const startEdit = (a) => { setEditingId(a.id); setEditForm({ owner: a.owner || '', quality_score: a.quality_score || '' }); };
  const saveEdit = async () => {
    try {
      const payload = {};
      if (editForm.owner) payload.owner = editForm.owner;
      if (editForm.quality_score) payload.quality_score = parseInt(editForm.quality_score);
      await purviewService.updateAsset(editingId, payload);
      setEditingId(null);
    } catch (e) { console.error(e); }
  };

  if (!data) return <LoadingState />;
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <SectionTitle>Catálogo de Assets</SectionTitle>
        <input type="text" placeholder="Buscar asset..." value={search} onChange={e => setSearch(e.target.value)}
          className="ml-auto px-3 py-1.5 border border-gray-200 rounded-lg text-sm w-64" />
      </div>
      <p className="text-xs text-gray-400 mb-3">💡 Campos vazios (Owner, Qualidade, Linhagem) podem ser preenchidos rodando o scan ou editando inline clicando no ✏️</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-gray-500">
            <th className="pb-2">Nome</th><th className="pb-2">Tipo</th><th className="pb-2">Domínio</th>
            <th className="pb-2">Classificação</th><th className="pb-2">Owner</th><th className="pb-2">Qualidade</th><th className="pb-2">Linhagem</th><th className="pb-2 w-8"></th>
          </tr></thead>
          <tbody>
            {filtered.map(a => (
              <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 font-medium">{a.name}</td>
                <td className="py-2 text-gray-500">{a.type}</td>
                <td className="py-2"><span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{a.domain || '—'}</span></td>
                <td className="py-2"><span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{a.classification || '—'}</span></td>
                <td className="py-2">
                  {editingId === a.id ? (
                    <input value={editForm.owner} onChange={e => setEditForm({...editForm, owner: e.target.value})}
                      className="w-20 px-1 py-0.5 border rounded text-xs" placeholder="Owner" />
                  ) : (
                    <span className={a.owner ? 'text-gray-600' : 'text-gray-300 italic'}>{a.owner || 'vazio'}</span>
                  )}
                </td>
                <td className="py-2">
                  {editingId === a.id ? (
                    <input value={editForm.quality_score} onChange={e => setEditForm({...editForm, quality_score: e.target.value})}
                      className="w-12 px-1 py-0.5 border rounded text-xs" placeholder="0-100" type="number" />
                  ) : (
                    a.quality_score != null ? (
                      <span className={`font-medium ${a.quality_score >= 85 ? 'text-green-600' : a.quality_score >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                        {a.quality_score}%
                      </span>
                    ) : <span className="text-gray-300 italic">—</span>
                  )}
                </td>
                <td className="py-2">{a.has_lineage ? <CheckCircle2 size={14} className="text-green-500" /> : <XCircle size={14} className="text-gray-300" />}</td>
                <td className="py-2">
                  {editingId === a.id ? (
                    <div className="flex gap-1">
                      <button onClick={saveEdit} className="text-emerald-600"><CheckCircle2 size={14} /></button>
                      <button onClick={() => setEditingId(null)} className="text-gray-400"><X size={14} /></button>
                    </div>
                  ) : (
                    <button onClick={() => startEdit(a)} className="text-gray-300 hover:text-emerald-600"><Edit2 size={12} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ClassificationsTab = ({ data }) => {
  const [detail, setDetail] = useState(null);
  const [detailAssets, setDetailAssets] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const openDetail = async (cls) => {
    setDetail(cls);
    setLoadingDetail(true);
    try {
      const res = await purviewService.getClassificationAssets(cls.name);
      setDetailAssets(res.data || []);
    } catch { setDetailAssets([]); }
    setLoadingDetail(false);
  };

  if (!data) return <LoadingState />;

  if (detail) {
    return (
      <div>
        <button onClick={() => setDetail(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft size={14} /> Voltar
        </button>
        <SectionTitle>
          <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: detail.color }} />
          {detail.name} — {detailAssets.length} assets
        </SectionTitle>
        {loadingDetail ? <LoadingState /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-500">
                <th className="pb-2">Nome</th><th className="pb-2">Tipo</th><th className="pb-2">Domínio</th><th className="pb-2">Owner</th><th className="pb-2">Rows</th>
              </tr></thead>
              <tbody>
                {detailAssets.map(a => (
                  <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 font-medium">{a.name}</td>
                    <td className="py-2 text-gray-500">{a.type}</td>
                    <td className="py-2"><span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{a.domain}</span></td>
                    <td className="py-2 text-gray-500">{a.owner || <span className="italic text-gray-300">—</span>}</td>
                    <td className="py-2 text-gray-500">{a.row_count?.toLocaleString() || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <SectionTitle>Classificações de Sensibilidade</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.map(c => (
          <div key={c.id} onClick={() => openDetail(c)}
            className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
              <span className="font-bold text-gray-900 group-hover:text-emerald-700">{c.name}</span>
              <ChevronRight size={14} className="ml-auto text-gray-300 group-hover:text-emerald-500" />
            </div>
            <p className="text-xs text-gray-500 mb-3">{c.description}</p>
            <p className="text-lg font-bold" style={{ color: THEME.primary }}>{c.asset_count} <span className="text-xs font-normal text-gray-400">assets</span></p>
          </div>
        ))}
      </div>
    </div>
  );
};

const LineageTab = ({ data }) => {
  if (!data) return <LoadingState />;
  const { nodes = [], edges = [] } = data;
  if (!nodes.length) return <div className="text-center text-gray-400 py-12">Selecione um asset no Catálogo para ver linhagem</div>;
  return (
    <div>
      <SectionTitle>Linhagem — {data.asset_name || 'Asset'}</SectionTitle>
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col items-center gap-2">
          {nodes.map((n, i) => (
            <React.Fragment key={n.id}>
              <div className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                n.type === 'source' ? 'bg-blue-50 border-blue-200 text-blue-800' :
                n.type === 'process' ? 'bg-purple-50 border-purple-200 text-purple-800' :
                n.type === 'table' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                n.type === 'api' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                'bg-gray-50 border-gray-200 text-gray-800'
              }`}>
                {n.label}
              </div>
              {i < nodes.length - 1 && <div className="text-gray-300">↓</div>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const QualityTab = ({ data }) => {
  const [detail, setDetail] = useState(null);
  const [detailAssets, setDetailAssets] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixMsg, setFixMsg] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupDomain, setGroupDomain] = useState('');
  const [formValues, setFormValues] = useState({ owner: '', sensitivity: '', has_lineage: true, has_tenant_id: true, quality_score: 80 });

  const openDetail = async (metric) => {
    setDetail(metric);
    setLoadingDetail(true);
    setSelectedIds([]);
    setGroupDomain('');
    setFixMsg('');
    try {
      const res = await purviewService.getQualityDetail(metric.metric);
      setDetailAssets(res.data || []);
    } catch { setDetailAssets([]); }
    setLoadingDetail(false);
  };

  const buildFixValues = () => {
    if (!detail) return {};
    switch (detail.metric) {
      case 'Ownership':
        return { owner: formValues.owner };
      case 'Classificação':
        return { sensitivity: formValues.sensitivity };
      case 'Linhagem':
        return { has_lineage: !!formValues.has_lineage };
      case 'Multi-tenant (RLS)':
        return { has_tenant_id: !!formValues.has_tenant_id };
      case 'Quality Score':
        return { quality_score: Number(formValues.quality_score || 0) };
      default:
        return {};
    }
  };

  const applyFix = async (mode) => {
    if (!detail) return;
    setFixing(true);
    setFixMsg('');
    try {
      const payload = {
        mode,
        asset_ids: mode === 'single' ? selectedIds : [],
        domain: mode === 'group' ? groupDomain : null,
        values: buildFixValues(),
      };
      const res = await purviewService.fixQualityMetric(detail.metric, payload);
      const affected = res?.data?.affected ?? 0;
      setFixMsg(`✅ ${affected} item(ns) corrigido(s).`);
      await openDetail(detail);
    } catch (e) {
      setFixMsg('❌ Erro ao aplicar correção. Verifique os campos e tente novamente.');
    }
    setFixing(false);
  };

  const handleAutoFix = async (action = 'all') => {
    setFixing(true); setFixMsg('');
    try {
      const res = await purviewService.qualityAutoFix(action);
      setFixMsg(`✅ ${res.data?.fixes_applied || 0} correções aplicadas. Recarregue a página para ver o resultado.`);
    } catch (e) { setFixMsg('❌ Erro ao aplicar correções'); }
    setFixing(false);
  };

  if (!data) return <LoadingState />;

  if (detail) {
    const domains = [...new Set((detailAssets || []).map(a => a.domain).filter(Boolean))];
    return (
      <div>
        <button onClick={() => setDetail(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft size={14} /> Voltar
        </button>
        <SectionTitle>{detail.metric} — {detailAssets.length} {detail.missing_label || 'assets'}</SectionTitle>
        <p className="text-sm text-gray-500 mb-4">Score atual: <span className="font-bold">{detail.score}%</span> · Para chegar a 100%, corrija os itens abaixo:</p>
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 space-y-3">
          <p className="text-xs font-semibold text-gray-600 uppercase">Formulário de correção</p>
          {detail.metric === 'Ownership' && (
            <input value={formValues.owner} onChange={(e) => setFormValues(prev => ({ ...prev, owner: e.target.value }))}
              placeholder="Owner (ex: Dados/Swimming)" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          )}
          {detail.metric === 'Classificação' && (
            <select value={formValues.sensitivity} onChange={(e) => setFormValues(prev => ({ ...prev, sensitivity: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
              <option value="">Selecione a classificação</option>
              <option value="Público">Público</option>
              <option value="Interno">Interno</option>
              <option value="Confidencial">Confidencial</option>
              <option value="Pessoal/LGPD">Pessoal/LGPD</option>
              <option value="Saúde">Saúde</option>
              <option value="Biometria/Performance">Biometria/Performance</option>
            </select>
          )}
          {detail.metric === 'Linhagem' && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!formValues.has_lineage}
              onChange={(e) => setFormValues(prev => ({ ...prev, has_lineage: e.target.checked }))} /> Marcar como com linhagem</label>
          )}
          {detail.metric === 'Multi-tenant (RLS)' && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!formValues.has_tenant_id}
              onChange={(e) => setFormValues(prev => ({ ...prev, has_tenant_id: e.target.checked }))} /> Marcar como com tenant_id/RLS</label>
          )}
          {detail.metric === 'Quality Score' && (
            <input type="number" min="0" max="100" value={formValues.quality_score}
              onChange={(e) => setFormValues(prev => ({ ...prev, quality_score: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <button disabled={fixing || selectedIds.length === 0} onClick={() => applyFix('single')}
              className="text-xs px-3 py-2 rounded-lg bg-emerald-600 text-white disabled:opacity-50">Resolver selecionados</button>
            <div className="flex gap-2">
              <select value={groupDomain} onChange={(e) => setGroupDomain(e.target.value)} className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-xs">
                <option value="">Domínio (grupo)</option>
                {domains.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <button disabled={fixing || !groupDomain} onClick={() => applyFix('group')}
                className="text-xs px-3 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50">Resolver grupo</button>
            </div>
            <button disabled={fixing} onClick={() => applyFix('all')}
              className="text-xs px-3 py-2 rounded-lg bg-amber-600 text-white disabled:opacity-50">Resolver tudo</button>
          </div>
          {fixMsg && <p className={`text-xs ${fixMsg.startsWith('✅') ? 'text-emerald-700' : 'text-red-600'}`}>{fixMsg}</p>}
        </div>
        {loadingDetail ? <LoadingState /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-500">
                <th className="pb-2 w-10"><input type="checkbox" checked={detailAssets.length > 0 && selectedIds.length === detailAssets.length}
                  onChange={(e) => setSelectedIds(e.target.checked ? detailAssets.map(a => a.id) : [])} /></th>
                <th className="pb-2">Asset</th><th className="pb-2">Tipo</th><th className="pb-2">Domínio</th><th className="pb-2">Classificação</th><th className="pb-2">Owner</th>
              </tr></thead>
              <tbody>
                {detailAssets.map(a => (
                  <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2"><input type="checkbox" checked={selectedIds.includes(a.id)}
                      onChange={(e) => setSelectedIds(prev => e.target.checked ? [...prev, a.id] : prev.filter(id => id !== a.id))} /></td>
                    <td className="py-2 font-medium">{a.name}</td>
                    <td className="py-2 text-gray-500">{a.type}</td>
                    <td className="py-2"><span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{a.domain || '—'}</span></td>
                    <td className="py-2 text-gray-500">{a.classification || <span className="italic text-gray-300">ausente</span>}</td>
                    <td className="py-2 text-gray-500">{a.owner || <span className="italic text-gray-300">ausente</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Scorecards de Qualidade</SectionTitle>
        <div className="flex gap-2">
          <button onClick={() => handleAutoFix('ownership')} disabled={fixing}
            className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50">
            👤 Fix Owners
          </button>
          <button onClick={() => handleAutoFix('lineage')} disabled={fixing}
            className="text-xs px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50">
            🔗 Fix Lineage
          </button>
          <button onClick={() => handleAutoFix('all')} disabled={fixing}
            className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium">
            {fixing ? '⏳ Aplicando...' : '🔧 Corrigir Tudo'}
          </button>
        </div>
      </div>
      {fixMsg && <p className={`text-xs mb-3 px-3 py-2 rounded-lg ${fixMsg.startsWith('✅') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{fixMsg}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.map(q => (
          <div key={q.metric} onClick={() => openDetail(q)}
            className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-all group">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-900 group-hover:text-emerald-700">{q.metric}</span>
              <div className="flex items-center gap-1">
                <span className={`text-xs ${q.trend === 'up' ? 'text-green-500' : q.trend === 'down' ? 'text-red-500' : 'text-gray-400'}`}>
                  {q.trend === 'up' ? '↑' : q.trend === 'down' ? '↓' : '→'}
                </span>
                <ChevronRight size={14} className="text-gray-300 group-hover:text-emerald-500" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">{q.description}</p>
            {q.missing_count != null && <p className="text-xs text-amber-600 mb-2">⚠ {q.missing_count} {q.missing_label}</p>}
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="h-2 rounded-full" style={{ width: `${q.score}%`, backgroundColor: q.score >= 85 ? '#059669' : q.score >= 70 ? '#f59e0b' : '#dc2626' }} />
            </div>
            <p className="text-right text-xs font-bold mt-1" style={{ color: q.score >= 85 ? '#059669' : q.score >= 70 ? '#f59e0b' : '#dc2626' }}>{q.score}%</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const PoliciesTab = ({ data }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const startEdit = (p) => { setEditing(p.id); setForm({ ...p }); };
  const saveEdit = async () => {
    try {
      await purviewService.updatePolicy(editing, form);
      setEditing(null);
    } catch (e) { console.error(e); }
  };

  if (!data) return <LoadingState />;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Políticas de Acesso</SectionTitle>
      </div>
      <div className="space-y-3">
        {data.map(p => (
          <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-4">
            {editing === p.id ? (
              <div className="space-y-3">
                <input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})}
                  className="w-full px-3 py-1.5 border rounded-lg text-sm" />
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-gray-500">Enforcement:</span>
                  {['block', 'warn', 'monitor_only'].map(e => (
                    <button key={e} onClick={() => setForm({...form, enforcement: e})}
                      className={`text-xs px-2 py-1 rounded ${form.enforcement === e ? 'bg-emerald-100 text-emerald-700 font-bold' : 'bg-gray-100 text-gray-500'}`}>
                      {e}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={saveEdit} className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-lg flex items-center gap-1"><Save size={12} /> Salvar</button>
                  <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 bg-gray-100 rounded-lg">Cancelar</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Lock size={16} className="text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{p.name}</p>
                  <p className="text-xs text-gray-500">Tipo: {p.data_type} · Roles: {p.allowed_roles.join(', ')}</p>
                </div>
                <StatusBadge status={p.enforcement} />
                <button onClick={() => startEdit(p)} className="text-gray-400 hover:text-emerald-600"><Edit2 size={14} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const GlossaryTab = ({ data }) => {
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState('');

  const handleSync = async () => {
    setSyncing(true); setMsg('');
    try {
      const res = await purviewService.syncGlossary();
      setMsg(res.meta?.message || 'Sincronizado!');
    } catch { setMsg('Erro ao sincronizar'); }
    setSyncing(false);
  };

  if (!data) return <LoadingState />;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Glossário de Negócio</SectionTitle>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs text-emerald-600">{msg}</span>}
          <button onClick={handleSync} disabled={syncing}
            className="flex items-center gap-1 text-xs px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 disabled:opacity-50">
            <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> Atualizar
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-gray-500">
            <th className="pb-2">Termo</th><th className="pb-2">Descrição</th><th className="pb-2">Domínio</th><th className="pb-2">Owner</th>
          </tr></thead>
          <tbody>
            {data.map(g => (
              <tr key={g.id} className="border-b border-gray-100">
                <td className="py-2 font-medium">{g.term}</td>
                <td className="py-2 text-gray-600">{g.description}</td>
                <td className="py-2"><span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{g.domain}</span></td>
                <td className="py-2 text-gray-500">{g.owner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ScansTab = ({ data }) => {
  const [running, setRunning] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [msg, setMsg] = useState('');

  const handleRun = async (sourceId) => {
    setRunning(sourceId); setMsg('');
    try {
      const res = await purviewService.runScan(sourceId);
      setMsg(res.meta?.message || 'Scan iniciado!');
      // Auto-show history and poll a few times for completion/status updates
      setShowHistory(true);
      let tries = 0;
      const timer = setInterval(async () => {
        tries += 1;
        try {
          const hist = await purviewService.getScanHistory();
          const rows = hist.data || [];
          setHistory(rows);
          const hasRunning = rows.some(r => r.status === 'running');
          if (!hasRunning || tries >= 8) clearInterval(timer);
        } catch {
          if (tries >= 8) clearInterval(timer);
        }
      }, 3000);
    } catch { setMsg('Erro ao iniciar scan'); }
    setTimeout(() => setRunning(null), 2000);
  };

  const loadHistory = async () => {
    setShowHistory(true);
    try {
      const res = await purviewService.getScanHistory();
      setHistory(res.data || []);
    } catch { setHistory([]); }
  };

  if (!data) return <LoadingState />;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Scans e Jobs</SectionTitle>
        <button onClick={loadHistory}
          className="flex items-center gap-1 text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200">
          <Clock size={12} /> Histórico
        </button>
      </div>
      {msg && <p className="text-xs text-emerald-600 mb-3 bg-emerald-50 px-3 py-2 rounded-lg">{msg}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-gray-500">
            <th className="pb-2">Fonte</th><th className="pb-2">Frequência</th><th className="pb-2">Status</th>
            <th className="pb-2">Última Execução</th><th className="pb-2">Falhas</th><th className="pb-2">Novos</th><th className="pb-2">Ação</th>
          </tr></thead>
          <tbody>
            {data.map(s => (
              <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 font-medium">{s.source}</td>
                <td className="py-2 text-gray-500">{s.frequency}</td>
                <td className="py-2"><StatusBadge status={s.status} /></td>
                <td className="py-2 text-gray-500">{s.last_run ? new Date(s.last_run).toLocaleDateString('pt-BR') : '—'}</td>
                <td className="py-2">{s.failures > 0 ? <span className="text-red-600 font-medium">{s.failures}</span> : '0'}</td>
                <td className="py-2">{s.new_assets}</td>
                <td className="py-2">
                  <button onClick={() => handleRun(s.source || s.id)} disabled={running === (s.source || s.id)}
                    className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1 disabled:opacity-50">
                    <Play size={10} className={running === (s.source || s.id) ? 'animate-pulse' : ''} /> {running === (s.source || s.id) ? 'Running...' : 'Run'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showHistory && (
        <div className="mt-6">
          <SectionTitle>Histórico de Execuções</SectionTitle>
          {history.length === 0 ? <p className="text-sm text-gray-400">Nenhuma execução registrada.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500">
                  <th className="pb-2">Fonte</th><th className="pb-2">Início</th><th className="pb-2">Fim</th>
                  <th className="pb-2">Status</th><th className="pb-2">Assets</th><th className="pb-2">Novos</th><th className="pb-2">Alertas</th><th className="pb-2">Erro</th>
                </tr></thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} className="border-b border-gray-100">
                      <td className="py-2 font-medium">{h.source}</td>
                      <td className="py-2 text-gray-500">{h.started_at ? new Date(h.started_at).toLocaleString('pt-BR') : '—'}</td>
                      <td className="py-2 text-gray-500">{h.finished_at ? new Date(h.finished_at).toLocaleString('pt-BR') : '—'}</td>
                      <td className="py-2"><StatusBadge status={h.status === 'completed' ? 'ok' : h.status} /></td>
                      <td className="py-2">{h.assets_found}</td>
                      <td className="py-2">{h.assets_new}</td>
                      <td className="py-2">{h.alerts_generated}</td>
                      <td className="py-2 text-xs text-red-600 max-w-xs truncate" title={h.error_message || ''}>{h.error_message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AlertsTab = ({ data, onAction, onBulkResolve }) => {
  const [selected, setSelected] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulking, setBulking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionForm, setActionForm] = useState({ status: 'acknowledged', note: '', follow_up_at: '' });

  const openDetail = async (alertId) => {
    setLoadingDetail(true);
    try {
      const res = await purviewService.getAlertDetail(alertId);
      setSelected(res.data);
      setSuggestions(res.data?.suggestions || []);
      setActionForm({ status: 'acknowledged', note: '', follow_up_at: '' });
    } catch { setSelected(null); }
    setLoadingDetail(false);
  };

  const submitAction = async (statusOverride = null) => {
    if (!selected || !onAction) return;
    const payload = {
      status: statusOverride || actionForm.status,
      note: actionForm.note || null,
      follow_up_at: actionForm.follow_up_at || null,
    };
    setSubmitting(true);
    try {
      await onAction(selected.id, payload);
      setSelected(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkResolve = async () => {
    setBulking(true);
    try {
      const res = await purviewService.bulkResolveAlerts();
      setBulkResult(res.data);
      if (onBulkResolve) onBulkResolve(res.data);
    } catch (e) { console.error(e); }
    setBulking(false);
  };

  if (!data) return <LoadingState />;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Alertas e Riscos ({data.length})</SectionTitle>
        {data.length > 5 && (
          <button onClick={handleBulkResolve} disabled={bulking}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium">
            <CheckCircle2 size={12} /> {bulking ? 'Resolvendo...' : 'Resolver Fáceis'}
          </button>
        )}
      </div>

      {bulkResult && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm">
          <p className="font-medium text-emerald-800">✅ {bulkResult.auto_resolved} alertas resolvidos automaticamente</p>
          {bulkResult.total_remaining > 0 && (
            <p className="text-emerald-600 text-xs mt-1">⚠ {bulkResult.total_remaining} alertas precisam da sua decisão (sensíveis/críticos)</p>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-bold text-gray-900">{selected.title}</h3>
                <p className="text-xs text-gray-500 mt-1">{selected.category} · {selected.severity}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            {selected.description && <p className="text-sm text-gray-700 mb-4 bg-gray-50 p-3 rounded-lg">{selected.description}</p>}
            <p className="text-xs font-bold text-gray-600 uppercase mb-2">Ações sugeridas:</p>
            <div className="space-y-2">
              {suggestions.map((s, i) => (
                <button key={i}
                  onClick={() => {
                    if (String(s.action).includes('dismiss')) submitAction('dismissed');
                    else if (String(s.action).includes('acknowledge')) submitAction('acknowledged');
                    else submitAction('resolved');
                  }}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    s.risk === 'none' ? 'bg-green-400' : s.risk === 'low' ? 'bg-blue-400' : 'bg-amber-400'
                  }`} />
                  <span className="text-sm font-medium text-gray-800">{s.label}</span>
                  {s.auto && <span className="ml-auto text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">auto</span>}
                </button>
              ))}
            </div>
            <div className="mt-4 p-3 border rounded-lg bg-gray-50 space-y-3">
              <p className="text-xs font-semibold text-gray-600 uppercase">Decisão manual</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <label className="flex items-center gap-2 p-2 rounded border bg-white">
                  <input type="radio" name="alert_status" checked={actionForm.status === 'resolved'} onChange={() => setActionForm(prev => ({ ...prev, status: 'resolved' }))} />
                  Resolver agora
                </label>
                <label className="flex items-center gap-2 p-2 rounded border bg-white">
                  <input type="radio" name="alert_status" checked={actionForm.status === 'dismissed'} onChange={() => setActionForm(prev => ({ ...prev, status: 'dismissed' }))} />
                  Ignorar
                </label>
                <label className="flex items-center gap-2 p-2 rounded border bg-white">
                  <input type="radio" name="alert_status" checked={actionForm.status === 'acknowledged'} onChange={() => setActionForm(prev => ({ ...prev, status: 'acknowledged' }))} />
                  Reconhecer (investigar depois)
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={actionForm.note}
                  onChange={(e) => setActionForm(prev => ({ ...prev, note: e.target.value }))}
                  placeholder="Observação (opcional)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <input
                  type="datetime-local"
                  value={actionForm.follow_up_at}
                  onChange={(e) => setActionForm(prev => ({ ...prev, follow_up_at: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="mt-4 pt-3 border-t flex gap-2">
              <button onClick={() => submitAction()} disabled={submitting}
                className="flex-1 text-sm py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-medium disabled:opacity-50">{submitting ? 'Salvando...' : 'Salvar decisão'}</button>
              <button onClick={() => setSelected(null)}
                className="flex-1 text-sm py-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium">Fechar</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {data.map(a => (
          <div key={a.id} className={`rounded-xl border p-4 flex items-start gap-3 cursor-pointer hover:shadow-md transition-all ${
            a.severity === 'critical' ? 'border-red-200 bg-red-50' :
            a.severity === 'high' ? 'border-orange-200 bg-orange-50' :
            'border-yellow-200 bg-yellow-50'
          }`} onClick={() => openDetail(a.id)}>
            <AlertTriangle size={16} className={a.severity === 'critical' ? 'text-red-500' : a.severity === 'high' ? 'text-orange-500' : 'text-yellow-500'} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{a.title || a.message}</p>
              {a.description && <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">{a.description}</p>}
              <p className="text-xs text-gray-400 mt-1">
                {a.category && <span className="bg-gray-100 px-1.5 py-0.5 rounded mr-2">{a.category}</span>}
                {a.created_at && new Date(a.created_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <StatusBadge status={a.severity} />
              {a.status && <StatusBadge status={a.status} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AuditTab = ({ data }) => {
  if (!data) return <LoadingState />;
  return (
    <div>
      <SectionTitle>Eventos de Auditoria</SectionTitle>
      <div className="space-y-2">
        {data.map(e => (
          <div key={e.id} className="flex items-center gap-3 py-2 border-b border-gray-100">
            <Clock size={14} className="text-gray-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-gray-900">{e.event.replace(/_/g, ' ')}</span>
              <span className="text-xs text-gray-500 ml-2">→ {e.target}</span>
              {e.details && <p className="text-xs text-gray-400">{e.details}</p>}
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap">{e.actor}</span>
            <span className="text-xs text-gray-300 whitespace-nowrap">{new Date(e.timestamp).toLocaleDateString('pt-BR')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AgentTab = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const sendMessage = async () => {
    if (!input.trim()) return;
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const res = await purviewService.chatPurviewAgent(input, messages);
      const reply = res.data?.reply || 'Sem resposta.';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: '❌ Erro ao chamar o agente. Verifique se AZURE_AI_ENDPOINT está configurado.' }]);
    }
    setLoading(false);
  };

  const quickActions = [
    "Quais assets estão sem owner?",
    "Resolva os alertas fáceis",
    "Como melhorar o quality score?",
    "Explique a linhagem do swimming_results",
  ];

  return (
    <div className="flex flex-col h-[600px]">
      <div className="flex items-center gap-2 mb-3">
        <Bot size={18} className="text-emerald-600" />
        <SectionTitle>Purview Agent</SectionTitle>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Pergunte sobre catálogo, alertas, qualidade, scans, LGPD ou peça para resolver problemas.
      </p>

      {/* Quick actions */}
      {messages.length === 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {quickActions.map((q, i) => (
            <button key={i} onClick={() => { setInput(q); }}
              className="text-xs px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 border border-emerald-200">
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto bg-white rounded-xl border border-gray-200 p-4 space-y-3 mb-3">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-300 text-sm">
            <Bot size={32} className="mr-2 opacity-50" /> Comece uma conversa...
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${
              m.role === 'user' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-800'
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-xl px-3 py-2 text-sm text-gray-400 animate-pulse">Pensando...</div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Pergunte ao Purview Agent..."
          className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-emerald-300 focus:ring-1 focus:ring-emerald-200 outline-none"
          disabled={loading}
        />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
          <Send size={14} />
        </button>
      </div>
    </div>
  );
};

const CostsTab = ({ data }) => {
  if (!data) return <LoadingState />;
  const { monthly_total, monthly_total_usd, items = [], projection_90d, note, purview_account, purview_sku, purview_region, source } = data;
  return (
    <div>
      <SectionTitle>Custos Microsoft Purview</SectionTitle>
      {/* Account info */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 flex items-center gap-3">
        <ShieldCheck size={20} className="text-emerald-700" />
        <div className="text-sm text-emerald-800">
          <span className="font-semibold">Account:</span> {purview_account || 'arena-stats'} &nbsp;|&nbsp;
          <span className="font-semibold">SKU:</span> {purview_sku || 'Standard'} &nbsp;|&nbsp;
          <span className="font-semibold">Região:</span> {purview_region || 'brazilsouth'} &nbsp;|&nbsp;
          <span className="font-semibold">Fonte:</span> {source === 'azure_cost_management' ? 'Azure Billing API' : source}
        </div>
      </div>
      {note && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-sm text-blue-800">
          <strong>ℹ️ Nota:</strong> {note}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4 text-center">
          <p className="text-2xl font-bold text-emerald-700">R$ {monthly_total?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-gray-500">Custo mensal (BRL)</p>
          {monthly_total_usd !== undefined && <p className="text-xs text-gray-400 mt-1">US$ {monthly_total_usd?.toFixed(2)}</p>}
        </div>
        <div className="bg-white rounded-xl border p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">R$ {projection_90d?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-gray-500">Projeção 90 dias</p>
        </div>
        <div className="bg-white rounded-xl border p-4 text-center">
          <p className="text-2xl font-bold text-gray-700">{items.length}</p>
          <p className="text-xs text-gray-500">Itens de custo</p>
        </div>
      </div>
      {items.length > 0 ? (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium text-gray-600">Serviço</th>
                <th className="px-4 py-2 font-medium text-gray-600">Descrição</th>
                <th className="px-4 py-2 font-medium text-gray-600 text-right">Valor (R$)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900">{item.item}</td>
                  <td className="px-4 py-2 text-gray-500">{item.description}</td>
                  <td className="px-4 py-2 text-right font-mono text-gray-700">{item.monthly?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border p-8 text-center text-gray-500">
          <DollarSign size={32} className="mx-auto mb-2 text-gray-300" />
          <p className="font-medium">Nenhum custo registrado neste período</p>
          <p className="text-xs mt-1">O Microsoft Purview está dentro do free tier — sem cobranças ativas.</p>
        </div>
      )}
    </div>
  );
};

const SettingsTab = ({ data }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { if (data) setForm({ ...data }); }, [data]);

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await purviewService.updatePurviewSettings(form);
      setMsg(res.meta?.message || 'Salvo!');
    } catch { setMsg('Erro ao salvar'); }
    setSaving(false);
  };

  if (!data || !form) return <LoadingState />;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Configurações do Purview</SectionTitle>
        <div className="flex items-center gap-2">
          {msg && <span className="text-xs text-emerald-600">{msg}</span>}
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1 text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            <Save size={12} /> Salvar
          </button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4 max-w-2xl">
        {Object.entries(form).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between gap-4 border-b border-gray-100 pb-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{key.replace(/_/g, ' ')}</span>
            <input
              value={value === null ? '' : Array.isArray(value) ? value.join(', ') : String(value)}
              onChange={e => setForm({ ...form, [key]: e.target.value || null })}
              className="text-sm text-gray-600 font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1 w-64 text-right"
              placeholder="não configurado"
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-4">
        💡 Alterações aqui serão refletidas no comportamento dos scans e classificações.
      </p>
    </div>
  );
};

const LoadingState = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: THEME.primary }} />
  </div>
);

// ─── Main Page ─────────────────────────────────────────────────────────────

const GovernancePurviewPage = () => {
  const { lang } = useGovernanceLang();
  const t = PURVIEW_LABELS[lang] || PURVIEW_LABELS.pt;
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab]);

  const loadTabData = async (tab, force = false) => {
    if (data[tab] && !force) return; // cached
    setLoading(true);
    try {
      let result;
      switch (tab) {
        case 'overview': result = await purviewService.getPurviewOverview(); break;
        case 'sources': result = await purviewService.listPurviewSources(); break;
        case 'catalog': result = await purviewService.listPurviewAssets(); break;
        case 'classifications': result = await purviewService.listClassifications(); break;
        case 'lineage': result = await purviewService.getAssetLineage('ast-002'); break;
        case 'quality': result = await purviewService.getPurviewQuality(); break;
        case 'policies': result = await purviewService.listPolicies(); break;
        case 'glossary': result = await purviewService.getPurviewGlossary(); break;
        case 'scans': result = await purviewService.listScans(); break;
        case 'alerts': result = await purviewService.listAlerts(); break;
        case 'costs': result = await purviewService.getPurviewCosts(); break;
        case 'agent': result = { data: 'ready' }; break;
        case 'audit': result = await purviewService.listAuditEvents(); break;
        case 'settings': result = await purviewService.getPurviewSettings(); break;
        default: result = { data: null };
      }
      setData(prev => ({ ...prev, [tab]: result?.data || null }));
    } catch (err) {
      console.error(`[Purview] Failed to load ${tab}:`, err);
      setData(prev => ({ ...prev, [tab]: null }));
    } finally {
      setLoading(false);
    }
  };

  const handleAlertAction = async (alertId, actionPayload) => {
    try {
      const payload = typeof actionPayload === 'string' ? { status: actionPayload } : actionPayload;
      await purviewService.updateAlertStatus(alertId, payload);
      const nextStatus = payload?.status;
      setData(prev => {
        const current = prev.alerts || [];
        if (nextStatus === 'resolved' || nextStatus === 'dismissed') {
          return { ...prev, alerts: current.filter(a => a.id !== alertId) };
        }
        return {
          ...prev,
          alerts: current.map(a => (a.id === alertId ? { ...a, status: nextStatus || a.status } : a))
        };
      });
    } catch (err) {
      console.error('[Purview] Failed to update alert:', err);
    }
  };

  const renderTab = () => {
    const d = data[activeTab];
    switch (activeTab) {
      case 'overview': return <OverviewTab data={d} />;
      case 'sources': return <SourcesTab data={d} />;
      case 'catalog': return <CatalogTab data={d} />;
      case 'classifications': return <ClassificationsTab data={d} />;
      case 'lineage': return <LineageTab data={d} />;
      case 'quality': return <QualityTab data={d} />;
      case 'policies': return <PoliciesTab data={d} />;
      case 'glossary': return <GlossaryTab data={d} />;
      case 'scans': return <ScansTab data={d} />;
      case 'alerts': return <AlertsTab data={d} onAction={handleAlertAction} onBulkResolve={() => setData(prev => ({...prev, alerts: null}))} />;
      case 'costs': return <CostsTab data={d} />;
      case 'agent': return <AgentTab />;
      case 'audit': return <AuditTab data={d} />;
      case 'settings': return <SettingsTab data={d} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.bg }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <h1 className="text-gray-900 text-xl font-bold">Purview — Data Governance</h1>
            <p className="text-xs text-gray-500">{t.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex flex-wrap gap-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium rounded-t-lg transition-all whitespace-nowrap ${
                  isActive ? 'border-b-2 text-emerald-800 bg-emerald-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                style={isActive ? { borderBottomColor: THEME.primary } : {}}
              >
                <Icon size={13} />
                {t.tabs[tab.key] || tab.key}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 max-w-7xl mx-auto">
        {loading && !data[activeTab] ? <LoadingState /> : renderTab()}
      </div>
    </div>
  );
};

export default GovernancePurviewPage;
